import React from 'react';
import PropTypes from 'prop-types';
import { useSupportRequests } from '../hooks/useSupportRequests';

const STATE_LABELS = {
  hubspot: { pending: 'Opening ticket', accepted: 'Ticket opened' },
  email: {
    pending: 'Pending',
    queued: 'Email queued',
    sending: 'Sending email',
    submitted: 'Email sent',
    suppressed: 'Email not sent',
    failed: 'Email failed'
  }
};

export const statusPrompt = (request) => `What's the status of support ticket #${request.ticket_id}?`;
export const followUpPrompt = (request) => `Add this to support ticket #${request.ticket_id}: [what you want to tell support]`;

const stateLabel = (request) => (
  request.hubspot_stage || (STATE_LABELS[request.channel] || {})[request.delivery_state] || request.delivery_state
);

const stateClass = (request) => {
  if (request.hubspot_stage) return request.hubspot_stage_closed ? 'closed' : 'open';
  return request.delivery_state;
};

const formatDate = (value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const ConductorSupportRequestsList = ({ onAskConductor }) => {
  const { supportRequests, loading, error, reload } = useSupportRequests();

  if (loading) {
    return <div className="venue-conductor-manual-panel-status">Loading your support requests…</div>;
  }

  if (error) {
    return (
      <div className="venue-conductor-manual-panel-status">
        <p>Couldn&apos;t load your support requests.</p>
        <button type="button" className="venue-conductor-manual-panel-retry" onClick={reload}>
          Try again
        </button>
      </div>
    );
  }

  if (!supportRequests) return null;

  if (supportRequests.length === 0) {
    return (
      <div className="venue-conductor-manual-panel-status">
        <p>You haven&apos;t sent a request to support from Conductor yet.</p>
        <p>When Conductor can&apos;t solve something, ask it to send the problem to support and the request will show up here.</p>
      </div>
    );
  }

  return (
    <ul className="venue-conductor-support-requests" aria-label="Support requests">
      {supportRequests.map((request) => (
        <li key={request.id} className="venue-conductor-support-request">
          <div className="venue-conductor-support-request-heading">
            <span className="venue-conductor-support-request-ticket">
              {request.ticket_id ? `Ticket #${request.ticket_id}` : 'Emailed to support'}
            </span>
            <span className="venue-conductor-support-request-date">{formatDate(request.created_at)}</span>
          </div>
          <div className="venue-conductor-support-request-subject">{request.subject}</div>
          <p className="venue-conductor-support-request-summary">{request.summary}</p>
          <div className="venue-conductor-support-request-meta">
            <span className={`venue-conductor-support-request-state venue-conductor-support-request-state-${stateClass(request)}`}>
              {stateLabel(request)}
            </span>
            {request.urgency && <span>{request.urgency} urgency</span>}
            {request.thread_title && <span>From &ldquo;{request.thread_title}&rdquo;</span>}
          </div>
          {request.ticket_id && (
            <div className="venue-conductor-support-request-actions">
              <button type="button" className="venue-conductor-support-request-action" onClick={() => onAskConductor(statusPrompt(request))}>
                Check status
              </button>
              <button type="button" className="venue-conductor-support-request-action" onClick={() => onAskConductor(followUpPrompt(request))}>
                Add details
              </button>
            </div>
          )}
        </li>
      ))}
    </ul>
  );
};

ConductorSupportRequestsList.propTypes = {
  onAskConductor: PropTypes.func.isRequired
};

export default ConductorSupportRequestsList;
